import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Login from './components/Login';
import Register from './components/Register';
import AdminDashboard from './components/AdminDashboard';
import UserDashboard from './components/UserDashboard';
import { FaMoon, FaSun, FaBook, FaSignOutAlt } from 'react-icons/fa';
import API_URLS from './apiUrls'; // Import your URL configuration

// Configure axios defaults
axios.defaults.withCredentials = true;
axios.defaults.headers.common['Content-Type'] = 'application/json';

function App() {
  const [user, setUser] = useState(null);
  const [entries, setEntries] = useState([]);
  const [showRegister, setShowRegister] = useState(false);
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem('darkMode') === 'true' || false
  );
  const [loading, setLoading] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Check if a user session exists on the server
  const checkAuth = async () => {
    try {
      setAuthChecking(true);
      const res = await axios.get(API_URLS.CHECK_AUTH);
      if (res.data.status === 'success') {
        setUser(res.data.user);
      } else {
        setUser(null);
        setEntries([]);
      }
    } catch (err) {
      setUser(null);
      setEntries([]);
    } finally {
      setAuthChecking(false);
    }
  };

  const fetchEntries = async () => {
    if (!user) {
      setEntries([]);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(API_URLS.ENTRIES);
      setEntries(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
        setEntries([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(API_URLS.LOGOUT);
    } catch (err) {}
    setUser(null);
    setEntries([]);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user && !authChecking) {
      fetchEntries();
    }
  }, [user, authChecking]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Show loading screen while checking authentication
  if (authChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 transition-all duration-300 flex flex-col justify-center items-center">
        <div className="flex items-center justify-center mb-8">
          <FaBook className="text-teal-500 dark:text-teal-400 text-4xl mr-3" />
          <h1 className="text-3xl font-extrabold text-gray-800 dark:text-white">Cashbook</h1>
        </div>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 transition-all duration-300 flex items-center justify-center">
        <div className="absolute top-5 right-5">
          <button
            onClick={toggleDarkMode}
            className="p-3 rounded-full bg-white/80 dark:bg-gray-700/80 text-teal-600 dark:text-teal-300 shadow-md hover:shadow-lg transition-all"
            aria-label="Toggle dark mode"
          >
            {darkMode ? <FaSun size={18} /> : <FaMoon size={18} />}
          </button>
        </div>
        <div className="w-full max-w-md mx-auto">
          {showRegister ? (
            <Register onShowLogin={() => setShowRegister(false)} />
          ) : (
            <Login setUser={setUser} onShowRegister={() => setShowRegister(true)} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 text-gray-800 dark:text-gray-200 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <FaBook className="text-teal-600 dark:text-teal-400 text-2xl mr-3" />
            <h1 className="text-2xl font-bold text-teal-700 dark:text-teal-400">Cashbook</h1>
          </div>
          <div className="flex items-center space-x-4">
            <span className="hidden sm:inline-block text-sm font-medium">
              Hello, {user.username}
            </span>
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-full bg-white/80 dark:bg-gray-700/80 text-teal-600 dark:text-teal-300 shadow-sm hover:shadow-md transition-all"
              aria-label="Toggle dark mode"
            >
              {darkMode ? <FaSun size={18} /> : <FaMoon size={18} />}
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg shadow-sm hover:shadow-md transition-all"
            >
              <FaSignOutAlt className="mr-2" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
          {user.role === 'admin' ? (
            <AdminDashboard 
              darkMode={darkMode} 
              entries={entries}
              fetchEntries={fetchEntries}
              loading={loading}
            />
          ) : (
            <UserDashboard 
              user={user} 
              entries={entries} 
              fetchEntries={fetchEntries} 
              darkMode={darkMode}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
